import { FC, useEffect, useRef } from 'react';
import { useState } from 'react';
import PropTypes, { element } from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  MenuItem,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  ButtonGroup,
  Box,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@material-ui/core';
import { ExpandMore as ExpandMoreIcon } from '@material-ui/icons';
import { useDispatch, useSelector } from "react-redux";
import type { Customer } from '../../types/customer';
import { InputField } from '../input-field';
import { AutocompleteField } from '../autocomplete-field';
import { customerPostOne, customerPutOne, customerDeleteOne } from '../../api/review-1/review-1-customer';
import { XCircle as XCircleIcon } from '../../icons/x-circle';
import { ConfirmationDialog } from '../confirmation-dialog';
import { concernings } from '../../utils/concernings';
import { filterActiveConcernings } from '../../utils/concernings';
import { unknownPost, resetUnknownStatus, unknownDelete, removeSelectedMove, clearSelectedMoves, addSelectedConcerning, removeSelectedConcerning, clearSelectedConcernings } from '../../slices/unknownSlice';
import { review3GetAll } from '../../slices/review3Slice';
import { RootState } from '../../store';
import { useTheme } from "@material-ui/core/styles";
import { useTranslation } from 'react-i18next';
import { InfoPop } from '../info-pop';
import { PopoverConcerning } from '../popover-concerning';
import { openUnknownDialog } from 'src/slices/unknownSlice';
import { ChevronLeft as ChevronLeftIcon } from '../../icons/chevron-left';
import { ChevronRight as ChevronRightIcon } from '../../icons/chevron-right';
import { unknownGetAll } from 'src/api/unknown';
import { TableItemMenu } from '../table-item-menu';
import { UnknownMovesPanel } from './unknown-moves-panel';
import UnknownConcerningsPanel from './unknown-concernings-panel';
import { customerGetAll } from '../../api/review-1/review-1-customer';
import { companyGetAll } from '../../api/review-1/review-1-company';
import { useSubscriptionStatus } from '../../hooks/use-subscription-status';
import { LimitMessageWithUpgrade } from '../subscription/limit-message-with-upgrade';

interface UnknownDialogProps {
  open: boolean;
  onClose: () => void;
}

export const UnknownDialog: FC<UnknownDialogProps> = (props) => {
  const { t } = useTranslation();
  const { open, onClose, ...other } = props;
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const { unknownIsLoading, unknownStatus, unknownNextAction, selectedMoves, selectedConcernings } = useSelector((state: RootState) => state.unknown);
  const [showDetails, setShowDetails] = useState(true);
  const [showList, setShowList] = useState(true);
  const [showMovesPanel, setShowMovesPanel] = useState(false);
  const [showConcerningsPanel, setShowConcerningsPanel] = useState(false);
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const [height, setHeight] = useState(0)
  const mainGridRef = useRef(null)
  const [refVisible, setRefVisible] = useState(false)

  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!refVisible) {
      return
    }
    setHeight(mainGridRef.current.clientHeight)
  }, [refVisible, showDetails, mainGridRef?.current?.clientHeight])

  // Load clients and companies for chip display
  useEffect(() => {
    const loadClientsAndCompanies = async () => {
      try {
        const [clientsData, companiesData] = await Promise.all([
          customerGetAll(),
          companyGetAll()
        ]);

        console.log('Loaded clients data:', clientsData);
        console.log('Loaded companies data:', companiesData);

        if (clientsData !== 'error') {
          setClients(clientsData || []);
        }

        if (companiesData && !companiesData.err) {
          setCompanies(companiesData.items || []);
        }

        setDataLoaded(true);
      } catch (error) {
        console.error('Error loading clients and companies:', error);
        setDataLoaded(true);
      }
    };

    loadClientsAndCompanies();
  }, []);

  // Function to get display name for a concerning
  const getConcerningDisplayName = (concerningId: string | any) => {
    console.log('Getting display name for:', concerningId);
    console.log('Available clients:', clients);
    console.log('Available companies:', companies);

    // Handle case where concerningId is an object (from API)
    if (typeof concerningId === 'object' && concerningId !== null) {
      // If it's an object with a value property, use that
      if (concerningId.value) {
        return t(concernings[concerningId.value]?.label || concerningId.value);
      }
      // If it's an object with an id property, convert to string
      if (concerningId.id) {
        concerningId = concerningId.id.toString();
      } else {
        // Fallback for unknown object structure
        return t('Unknown concerning');
      }
    }

    // Ensure concerningId is a string
    const concerningIdStr = String(concerningId);

    // Check if it's a client
    if (concerningIdStr.startsWith('clients_')) {
      const clientId = concerningIdStr.replace('clients_', '');
      console.log('Looking for client with ID:', clientId);
      const client = clients.find(c => c.id == clientId || c.id === parseInt(clientId));
      console.log('Found client:', client);
      return client ? (client.name || client.description || `Client ${clientId}`) : concerningIdStr;
    }

    // Check if it's a company
    if (concerningIdStr.startsWith('companies_')) {
      const companyId = concerningIdStr.replace('companies_', '');
      console.log('Looking for company with ID:', companyId);
      const company = companies.find(c => c.id == companyId || c.id === parseInt(companyId));
      console.log('Found company:', company);
      return company ? (company.name || company.description || `Company ${companyId}`) : concerningIdStr;
    }

    // For static concernings, use the concernings mapping
    return t(concernings[concerningIdStr]?.label || concerningIdStr);
  };

  const unknown = useSelector((state: RootState) => state.unknown);
  const dispatch = useDispatch();
  const { hasPremium } = useSubscriptionStatus();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchedString, setSearchedString] = useState('');

  const FREE_TIER_UNKNOWN_LIMIT = 3;
  const isAtUnknownLimit = !hasPremium && total >= FREE_TIER_UNKNOWN_LIMIT;

  useEffect(() => {
    getItems();
  }, []);

  useEffect(() => {
    getItems();
  }, [searchedString]);

  const getItems = async () => {
    const res = await unknownGetAll('description', 'asc', searchedString, 1, 1000);
    console.log(res);
    if (res.items) {
      setItems(res.items);
      setTotal(res.total);
    } else {
      setItems([]);
      setTotal(0);
    }
  };

  let initialValues = {
    description: '',
    comment: '',
    outcome: '',
    concernings: [],
    positive_impact: -1,
    negative_impact: -1,
    no_go: 0,
    move: '',
    submit: null
  };

  if ((unknown.unknownId !== null && unknown.unknownId !== undefined) || unknown.isDuplicated === true) {
    initialValues.description = unknown.description;
    initialValues.comment = unknown.comment;
    initialValues.outcome = unknown.outcome;
    initialValues.concernings = unknown.concernings;
    initialValues.positive_impact = unknown.positive_impact;
    initialValues.negative_impact = unknown.negative_impact;
    initialValues.no_go = unknown.no_go;
    initialValues.move = unknown.move;
  }

  useEffect(() => {
    console.log('store status', unknownIsLoading);
    if (unknownStatus === 'success') {
      toast.success(`Unknown ${(unknown.unknownId !== null && unknown.unknownId !== undefined) ? 'updated' : 'created'}`);
      formik.resetForm();
      formik.setStatus({ success: true });
      formik.setSubmitting(false);
      dispatch(resetUnknownStatus());
      getItems(); // This will update the total count
      dispatch(review3GetAll());
      if (unknownNextAction === 'close') {
        onClose();
      } else {
        // "Save and create new": reset the dialog to a blank new unknown.
        dispatch(openUnknownDialog({ element: { id: null }, isEditabled: true, isDuplicated: false }));
      }
    } else {
      formik.setStatus({ success: false });
      formik.setErrors({ submit: unknownStatus ? t('unknown_save_error') : null });
      formik.setSubmitting(false);
      if (unknownStatus) {
        toast.error(t('unknown_save_error'));
      }
    }
  }, [unknownStatus]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: Yup.object().shape({
      description: Yup.string().min(3).max(255).required('Description is required'),
    }),
    onSubmit: async (values, helpers) => {
      try {
        // dispatch(review1PostCustomer(values));
      } catch (err) {
        console.error(err);
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
      }
    }
  });

  const handlePositiveImpact = (event: React.MouseEvent<HTMLElement>, newAlignment: number | null) => {
    formik.setFieldValue('positive_impact', newAlignment);
  };

  const handleNegativeImpact = (event: React.MouseEvent<HTMLElement>, newAlignment: number | null) => {
    formik.setFieldValue('negative_impact', newAlignment);
  };

  const handleNoGo = (event: React.ChangeEvent<HTMLInputElement>) => {
    // setChecked([checked[0], event.target.checked]);
    formik.setFieldValue('no_go', event.target.checked);
  };

  const handleSubmitAndQuit = () => {
    console.log('Submit and quit');
    // Check limit before creating (only for new Unknowns, not updates)
    if (!unknown.unknownId && isAtUnknownLimit) {
      toast.error(<LimitMessageWithUpgrade message={t('subscription_limit_reached', { limit: FREE_TIER_UNKNOWN_LIMIT })} />);
      return;
    }
    const dataWithMoves = {
      description: formik.values.description,
      comment: formik.values.comment || '',
      outcome: formik.values.outcome || '',
      positive_impact: formik.values.positive_impact !== undefined ? formik.values.positive_impact : -1,
      negative_impact: formik.values.negative_impact !== undefined ? formik.values.negative_impact : -1,
      no_go: formik.values.no_go !== undefined ? formik.values.no_go : 0,
      selectedMoves: selectedMoves,
      concernings: selectedConcernings.map(concerning => {
        // If concerning is already an object with id and value properties, use it as-is
        if (typeof concerning === 'object' && concerning !== null && concerning.id && concerning.value) {
          return concerning;
        }
        // Otherwise, wrap it in the expected format
        return {
          value: concerning,
          id: concerning
        };
      })
    };
    dispatch(unknownPost('close', dataWithMoves, unknown.unknownId));
  };

  const handleSubmitAndReset = () => {
    console.log('Submit and reset');
    // Check limit before creating (only for new Unknowns, not updates)
    if (!unknown.unknownId && isAtUnknownLimit) {
      toast.error(<LimitMessageWithUpgrade message={t('subscription_limit_reached', { limit: FREE_TIER_UNKNOWN_LIMIT })} />);
      return;
    }
    const dataWithMoves = {
      description: formik.values.description,
      comment: formik.values.comment || '',
      outcome: formik.values.outcome || '',
      positive_impact: formik.values.positive_impact !== undefined ? formik.values.positive_impact : -1,
      negative_impact: formik.values.negative_impact !== undefined ? formik.values.negative_impact : -1,
      no_go: formik.values.no_go !== undefined ? formik.values.no_go : 0,
      selectedMoves: selectedMoves,
      concernings: selectedConcernings.map(concerning => {
        // If concerning is already an object with id and value properties, use it as-is
        if (typeof concerning === 'object' && concerning !== null && concerning.id && concerning.value) {
          return concerning;
        }
        // Otherwise, wrap it in the expected format
        return {
          value: concerning,
          id: concerning
        };
      })
    };
    dispatch(unknownPost('new', dataWithMoves));
  };

  const handleUpdateConcernings = (newConcernings: any[]) => {
    formik.setFieldValue('concernings', newConcernings);
  };

  // Escape and the Cancel button must both go through the unsaved-changes confirmation.
  const handleCloseRequest = () => {
    if (formik.values.description !== '') {
      setOpenConfirmationDialog(true);
    } else {
      onClose();
    }
  };

  // NB: on ne peut pas se baser sur formik.isValid ici : l'effet ci-dessus pousse un
  // errors.submit (même à null), ce qui suffirait à rendre isValid faux en permanence.
  const saveDisabled = unknownIsLoading
    || !unknown.isEditabled
    || !!formik.errors.description
    || (formik.values.description || '').length < 3;



  return (
    <Dialog
      maxWidth="xl"
      fullWidth
      onClose={handleCloseRequest}
      open={open}
      TransitionProps={{
        onExited: () => formik.resetForm()
      }}
      PaperProps={{
        style: { borderRadius: 25 }
      }}
      {...other}
    >
      {/* <DialogTitle>
        {(unknown.unknownId !== null && unknown.unknownId !== undefined) ? `${t('update_unknown')} : ${unknown.description}` : t('create_unknown')}
        <div style={{ display: 'inline-block'}}>
          <InfoPop
            infoElementName={t('unknowns')}
            shortText='What is a Unknown?'
            position='right'
            longText='Unknowns exist when information or knowledge are missing. Once the missing information has been obtained, the Unknown no longer exists. Since Unknowns are only testifying of your ignorance, they have no probability of occurrence like it is the case for Risks.<br />
            Unknowns can relate to Customers, technology, market, regulations, Stakeholders, organizational issues,Resources, etc.<br />
            Once Unknowns have been identified, you can figure out the effort (time, money, people, etc.) needed to obtain the missing information/knowledge, which will eventually reduce the Unknowns. You should make sure to reduce Unknowns in the optimal sequence that consumes the least amount of resources (money, time, etc.). <br />
            To optimize the sequence you should establis an as complete as possible inventory of all identifiable Unknowns. <br />
            Any Unknown that has not been identified reduces your ability to realistically assess the effort required to reduce Unknowns and/or deliver the Definition of Success.'
          />
        </div>
      </DialogTitle> */}
      <DialogContent sx={{ padding: 0 }}>
        <Grid
          container
          spacing={2}
        // my={2}
        >
          <Grid item xs={showList ? 8 : 12} sx={{ p: 0, backgroundColor: "background.default" }}>
            <Grid container spacing={2} padding={4} sx={{ overflowY: "scroll" }} maxHeight={'80vh'} ref={el => { mainGridRef.current = el; setRefVisible(!!el); }}>
              <Grid item xs={12}>
                <Typography variant="h5" color={'error.main'}>
                  {(unknown.unknownId !== null && unknown.unknownId !== undefined) ? `${t('update_unknown')} : ${unknown.description}` : t('create_unknown')}
                  <div style={{ display: 'inline-block' }}>
                    <InfoPop
                      infoElementName={t('unknowns')}
                      shortText='What is a Unknown?'
                      position='right'
                      longText='Unknowns exist when information or knowledge are missing. Once the missing information has been obtained, the Unknown no longer exists. Since Unknowns are only testifying of your ignorance, they have no probability of occurrence like it is the case for Risks.<br />
                      Unknowns can relate to Customers, technology, market, regulations, Stakeholders, organizational issues,Resources, etc.<br />
                      Once Unknowns have been identified, you can figure out the effort (time, money, people, etc.) needed to obtain the missing information/knowledge, which will eventually reduce the Unknowns. You should make sure to reduce Unknowns in the optimal sequence that consumes the least amount of resources (money, time, etc.). <br />
                      To optimize the sequence you should establis an as complete as possible inventory of all identifiable Unknowns. <br />
                      Any Unknown that has not been identified reduces your ability to realistically assess the effort required to reduce Unknowns and/or deliver the Definition of Success.'
                    />
                  </div>
                  <div style={{ display: 'inline-block', textAlign: 'right', float: 'right' }}>
                    <IconButton onClick={() => setShowList(!showList)}>
                      {showList ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                  </div>
                </Typography>
              </Grid>

              {/* Section Navigation Tabs - REMOVED */}
              {/* <Grid item xs={12}>
                <Tabs 
                  value={currentSection} 
                  onChange={handleSectionChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab label={t('Details')} value="details" />
                  <Tab label={t('Actions')} value="moves" />
                </Tabs>
              </Grid> */}

              {/* Details Section - Always visible */}
              <Grid
                item
                xs={12}
                md={8}
              >
                <InputField
                  autoFocus
                  error={Boolean(formik.touched.description && formik.errors.description)}
                  fullWidth
                  helperText={formik.touched.description && formik.errors.description}
                  label={t('Description of the Unknown ')}
                  name="description"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  required
                  type="description"
                  value={formik.values.description}
                  multiline
                // sx={{'& .MuiFilledInput-root': {borderColor: 'red'}}}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <InputField
                  error={Boolean(formik.touched.no_go && formik.errors.no_go)}
                  fullWidth
                  helperText={formik.touched.no_go && formik.errors.no_go}
                  label={t('unknown_importance_label')}
                  name="no_go"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  required
                  value={formik.values.no_go}
                  select
                >
                  <MenuItem value={1}><span style={{ color: '#FF2200' }}>{t('no_go')}</span></MenuItem>
                  <MenuItem value={2}><span style={{ color: '#FF7A00' }}>{t('critical')}</span></MenuItem>
                  <MenuItem value={3}><span style={{ color: '#D7AC3D' }}>{t('important')}</span></MenuItem>
                  <MenuItem value={4}><span style={{ color: '#AA9F3D' }}>{t('minor')}</span></MenuItem>
                  <MenuItem value={0}><span style={{ color: '#8A8A8A' }}>?</span></MenuItem>
                </InputField>
              </Grid>
              <Grid item xs={12}>
                <InputField
                  error={Boolean(formik.touched.comment && formik.errors.comment)}
                  fullWidth
                  helperText={formik.touched.comment && formik.errors.comment}
                  label={t('Comments about this Unknown')}
                  name="comment"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type="comment"
                  value={formik.values.comment}
                  multiline
                // minRows={1}
                // sx={{'& .MuiFilledInput-root': {borderColor: 'red'}}}
                />
              </Grid>

              {/* Concernings Section */}
              <Grid item xs={12}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1
                }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t('Concerning')}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      if (showConcerningsPanel) {
                        setShowConcerningsPanel(false);
                      } else {
                        setShowMovesPanel(false);
                        setShowConcerningsPanel(true);
                      }
                    }}
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem'
                    }}
                  >
                    {showConcerningsPanel ? t('Hide') : t('Add')}
                  </Button>
                </Box>

                {/* Concernings List */}
                <Box sx={{
                  minHeight: 40,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 1.5,
                  backgroundColor: '#fafafa'
                }}>
                  {!dataLoaded ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {t('Loading concernings...')}
                    </Typography>
                  ) : filterActiveConcernings(selectedConcernings, clients, companies).length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {filterActiveConcernings(selectedConcernings, clients, companies).map((concerning, index) => (
                        <Chip
                          key={index}
                          label={getConcerningDisplayName(concerning)}
                          size="small"
                          color="error"
                          variant="outlined"
                          onDelete={() => dispatch(removeSelectedConcerning(concerning))}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {t('No concernings selected')}
                    </Typography>
                  )}
                </Box>
              </Grid>
              {/* <Grid item xs={12} textAlign="center">
                <Button
                  onClick={() => setShowDetails(!showDetails)}
                  type="button"
                  sx={{ color: 'error.main' }}
                >
                  {showDetails === true ? t('hide_details') : t('show_details')}
                </Button>
              </Grid> */}
              {(showDetails === true) ? (
                <>
                  <Grid item xs={12}>
                  <Accordion
                    disableGutters
                    sx={{ boxShadow: 'none', backgroundColor: 'transparent', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {t('Managing This Unknown')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0 }}>
                      <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <Typography
                      variant='body1'
                      sx={{ mb: 0.5 }}
                    >
                      {t('Potential positive impact')}
                    </Typography>
                    <ToggleButtonGroup
                      value={formik.values.positive_impact}
                      // onChange={formik.handleChange}
                      id="positive_impact"
                      exclusive
                      fullWidth
                      size='small'
                      onChange={handlePositiveImpact}
                      aria-label="text alignment"
                    >
                      <ToggleButton value={-1} id="positive_impact" aria-label="right aligned" sx={{ '&.Mui-selected': { backgroundColor: 'background.paper', color: 'text.primary' }, padding: 0.25, fontSize: "14px", borderTopLeftRadius: '100px', borderBottomLeftRadius: '100px' }}>
                        ?
                      </ToggleButton>
                      <ToggleButton value={0} id="positive_impact" aria-label="left aligned" sx={{ '&.Mui-selected': { backgroundColor: '#D2F2CD', color: '#49CC36' }, '&:hover': { backgroundColor: '#D2F2CD', color: '#49CC36' }, padding: 0.25, fontSize: "14px" }}>
                        {/* <FormatAlignLeftIcon /> */}
                        0
                      </ToggleButton>
                      <ToggleButton value={1} id="positive_impact" aria-label="left aligned" sx={{ '&.Mui-selected': { backgroundColor: '#A4E59B', color: '#FFF' }, '&:hover': { backgroundColor: '#A4E59B', color: '#FFF' }, padding: 0.25, fontSize: "14px" }}>
                        {/* <FormatAlignLeftIcon /> */}
                        +
                      </ToggleButton>
                      <ToggleButton value={2} id="positive_impact" aria-label="centered" sx={{ '&.Mui-selected': { backgroundColor: '#77D968', color: '#FFF' }, '&:hover': { backgroundColor: '#77D968', color: '#FFF' }, padding: 0.25, fontSize: "14px" }}>
                        {/* <FormatAlignCenterIcon /> */}
                        + +
                      </ToggleButton>
                      <ToggleButton value={3} id="positive_impact" aria-label="right aligned" sx={{ '&.Mui-selected': { backgroundColor: '#49CC36', color: '#FFF' }, '&:hover': { backgroundColor: '#49CC36', color: '#FFF' }, padding: 0.25, fontSize: "14px", borderTopRightRadius: '100px', borderBottomRightRadius: '100px' }}>
                        {/* <FormatAlignRightIcon /> */}
                        + + +
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <Typography
                      variant='body1'
                      sx={{ mb: 0.5 }}
                    >
                      {t('Potential negative impact')}
                    </Typography>
                    <ToggleButtonGroup
                      value={formik.values.negative_impact}
                      // onChange={formik.handleChange}
                      id="negative_impact"
                      exclusive
                      fullWidth
                      size='small'
                      onChange={handleNegativeImpact}
                      aria-label="text alignment"
                    >
                      <ToggleButton value={-1} id="negative_impact" aria-label="right aligned" sx={{ '&.Mui-selected': { backgroundColor: 'background.paper', color: 'text.primary' }, padding: 0.25, fontSize: "14px", borderTopLeftRadius: '100px', borderBottomLeftRadius: '100px' }}>
                        ?
                      </ToggleButton>
                      <ToggleButton value={0} id="negative_impact" aria-label="left aligned" sx={{ '&.Mui-selected': { backgroundColor: '#FFD3CC', color: '#FF4E33' }, '&:hover': { backgroundColor: '#FFD3CC', color: '#FF4E33' }, padding: 0.25, fontSize: "14px" }}>
                        {/* <FormatAlignLeftIcon /> */}
                        0
                      </ToggleButton>
                      <ToggleButton value={1} id="negative_impact" aria-label="left aligned" sx={{ '&.Mui-selected': { backgroundColor: '#FFA799', color: '#FFF' }, '&:hover': { backgroundColor: '#FFA799', color: '#FFF' }, padding: 0.25, fontSize: "14px" }}>
                        {/* <FormatAlignLeftIcon /> */}
                        -
                      </ToggleButton>
                      <ToggleButton value={2} id="negative_impact" aria-label="centered" sx={{ '&.Mui-selected': { backgroundColor: '#FF7A66', color: '#FFF' }, '&:hover': { backgroundColor: '#FF7A66', color: '#FFF' }, padding: 0.25, fontSize: "14px" }}>
                        {/* <FormatAlignCenterIcon /> */}
                        - -
                      </ToggleButton>
                      <ToggleButton value={3} id="negative_impact" aria-label="right aligned" sx={{ '&.Mui-selected': { backgroundColor: '#FF4E33', color: '#FFF' }, '&:hover': { backgroundColor: '#FF4E33', color: '#FFF' }, padding: 0.25, fontSize: "14px", borderTopRightRadius: '100px', borderBottomRightRadius: '100px' }}>
                        {/* <FormatAlignRightIcon /> */}
                        - - -
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                  {/* <Grid
                    item
                    xs={12}
                  >
                    <FormGroup>
                      <FormControlLabel control={<Checkbox checked={formik.values.noGo} onChange={handleNoGo} />} label={(
                        <>
                          {t('no_go')}
                          <div style={{ display: 'inline-block' }}>
                            <InfoPop
                              infoElementName={t('no_go')}
                              shortText='What is a No-Go?'
                              position='right'
                              longText='A No-Go is a show-stopper. When the impact of an Unknown is potentially critical enough to prevent the Project from being viable or sustainable, it is qualified as a “No-Go”. Qualifying an Unknown as a “No-go” will require to  reduce it as early as possible. Once a No-Go is confirmed, it means that the Project should be interrupted and that any further consumption of resources will most likely translate into a waste.e created.  To avoid this waste you should careful plan the sequence of your Plan for reducing Unknowns.'
                            />
                          </div>
                        </>)} />
                    </FormGroup>
                  </Grid> */}

                  <Divider />
                  {/* Tactical Actions Section */}
                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {t('Tactical Actions to reduce this Unknown')}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          if (showMovesPanel) {
                            setShowMovesPanel(false);
                          } else {
                            setShowConcerningsPanel(false);
                            setShowMovesPanel(true);
                          }
                        }}
                        sx={{
                          minWidth: 'auto',
                          px: 2,
                          py: 0.5,
                          fontSize: '0.75rem'
                        }}
                      >
                        {showMovesPanel ? t('Hide') : t('Add')}
                      </Button>
                    </Box>

                    {/* Actions List */}
                    <Box sx={{
                      minHeight: 40,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 1.5,
                      backgroundColor: '#fafafa'
                    }}>
                      {selectedMoves.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selectedMoves.map((move) => (
                            <Chip
                              key={move.id}
                              label={move.name}
                              size="small"
                              onDelete={() => dispatch(removeSelectedMove(move.id))}
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {t('No actions selected')}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <InputField
                      error={Boolean(formik.touched.outcome && formik.errors.outcome)}
                      fullWidth
                      helperText={formik.touched.outcome && formik.errors.outcome}
                      label={t('Result of Action to reduce Unknown (what has been learned)')}
                      name="outcome"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      type="outcome"
                      value={formik.values.outcome}
                      multiline
                      rows={1}
                    // sx={{'& .MuiFilledInput-root': {borderColor: 'red'}}}
                    />
                  </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
                  {formik.errors.submit && (
                    <Grid
                      item
                      xs={12}
                    >
                      <FormHelperText error>
                        {formik.errors.submit}
                      </FormHelperText>
                    </Grid>
                  )}
                </>
              ) : <></>}

              {/* Moves Section - REMOVED */}
              {/* {currentSection === 'moves' && (
                <Grid item xs={12}>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="h6" color="success.main" sx={{ mb: 2 }}>
                      {t('Select Related Actions')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('Choose existing actions that are related to this unknown. You can search by name, description, or comments.')}
                    </Typography>
                    {selectedMoves.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {t('Selected actions')} ({selectedMoves.length}):
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selectedMoves.map((move) => (
                            <Chip
                              key={move.id}
                              label={move.name}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )} */}

              <Grid><Divider /></Grid>
              <Grid item xs={12} textAlign="center">
                <Button
                  color="error"
                  size="small"
                  onClick={handleCloseRequest}
                  variant="outlined"
                  sx={{ mx: 1 }}
                >
                  {t('cancel')}
                </Button>
                <Button
                  color="error"
                  size="small"
                  disabled={saveDisabled}
                  onClick={() => { handleSubmitAndReset(); }}
                  variant="outlined"
                  sx={{ mx: 1 }}
                >
                  {t('unknown_save_and_new_label')}
                </Button>
                <Button
                  color="error"
                  size="small"
                  disabled={saveDisabled}
                  onClick={() => { handleSubmitAndQuit(); }}
                  variant="contained"
                  sx={{ mx: 1 }}
                >
                  {t('unknown_save_label')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          {showList && !showMovesPanel && !showConcerningsPanel && (
            <Grid item xs={4} sx={{ backgroundColor: 'rgba(236, 76, 71, 0.25)', overflowY: "scroll" }} padding={0} spacing={2} height={height}>
              <Grid container spacing={2} padding={4} sx={{ paddingLeft: 2 }}>
                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                  <Typography variant="h5" style={{ color: "rgb(236, 76, 71)" }}>
                    All unknowns <Chip color='default' label={total} size='small' variant='filled' />
                  </Typography>
                </Grid>
                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                  <Tooltip
                    title={
                      isAtUnknownLimit
                        ? <LimitMessageWithUpgrade message={t('subscription_limit_reached', { limit: FREE_TIER_UNKNOWN_LIMIT })} />
                        : ''
                    }
                    arrow
                  >
                    <span>
                      <Button
                        variant='contained'
                        size='small'
                        fullWidth
                        onClick={() => dispatch(openUnknownDialog({ element: { id: null }, isEditabled: true, isDuplicated: false }))}
                        style={{ backgroundColor: 'rgb(236, 76, 71)' }}
                        disabled={isAtUnknownLimit}
                      >
                        New unknown
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                  <InputField
                    fullWidth
                    placeholder='Search for an unknown'
                    name="search"
                    onChange={(e) => setSearchedString(e.target.value)}
                    type="text"
                    value={searchedString}
                  />
                </Grid>
                <Grid item xs={12}>
                  <List>
                    {items.map((u) => (
                      <ListItem disablePadding sx={{ backgroundColor: "background.default", marginBottom: 1, borderRadius: 1, padding: 0, fontSize: "10px" }}>
                        <ButtonGroup
                          variant="contained"
                          ref={anchorRef}
                          aria-label="Button group with a nested menu"
                          fullWidth
                          sx={{ padding: 0, fontSize: "10px", backgroundColor: 'white' }}
                          color='inherit'
                        >
                          <Button onClick={() => dispatch(openUnknownDialog({ element: u, isEditabled: true, isDuplicated: false }))} sx={{ backgroundColor: 'white', padding: '10px', textAlign: 'left' }}><span style={{ padding: '10px', width: '100%' }}>{u.description}</span></Button>
                          <Button
                            size="small"
                            aria-controls={open ? 'split-button-menu' : undefined}
                            aria-expanded={open ? 'true' : undefined}
                            aria-label="select merge strategy"
                            aria-haspopup="menu"
                            style={{ width: '1%' }}
                            sx={{ backgroundColor: 'white' }}
                          // onClick={handleToggle}
                          >
                            {/* <FontAwesomeIcon icon={faEllipsis as IconProp} /> */}
                            <TableItemMenu
                              onView={() => dispatch(openUnknownDialog({ element: u, isEditabled: false, isDuplicated: false }))}
                              onEdit={() => dispatch(openUnknownDialog({ element: u, isEditabled: true, isDuplicated: false }))}
                              onDuplicate={() => dispatch(openUnknownDialog({ element: u, isEditabled: true, isDuplicated: true }))}
                              onDelete={async () => {
                                await dispatch(unknownDelete(u.id));
                                getItems();
                              }}
                            />
                          </Button>
                        </ButtonGroup>
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </Grid>
          )}
          {showList && showMovesPanel && (
            <UnknownMovesPanel height={height} onHide={() => setShowMovesPanel(false)} />
          )}
          {showList && showConcerningsPanel && (
            <UnknownConcerningsPanel height={height} onHide={() => setShowConcerningsPanel(false)} />
          )}
        </Grid>
      </DialogContent>
      <Divider />
      {/* <DialogActions>
        <Button
          color="primary"
          size="small"
          onClick={onClose}
          variant="outlined"
        >
          {t('cancel')}
        </Button>
        <Button
          color="success"
          size="small"
          disabled={(unknownIsLoading || !unknown.isEditabled || (formik.values.description.length < 3))}
          onClick={() => { handleSubmitAndQuit(); }}
          variant="contained"
        >
          {(unknown.unknownId !== null && unknown.unknownId !== undefined) ? t('update') : t('create')}
        </Button>
      </DialogActions> */}
      <ConfirmationDialog
        message={t('do_you_want_to_close_without_saving')}
        onCancel={() => { setOpenConfirmationDialog(false) }}
        onConfirm={() => {
          setOpenConfirmationDialog(false);
          onClose();
        }}
        open={openConfirmationDialog}
        title={t('confirmation_title')}
        variant='warning'
      />
    </Dialog >
  );
};

UnknownDialog.defaultProps = {
  open: false
};

UnknownDialog.propTypes = {
  // @ts-ignore
  open: PropTypes.bool,
  onClose: PropTypes.func
};
